import { useEffect, useState } from "react";
import type { AutoDormCheckSettings } from "../types";

export type SchoolCheckInWindow = Pick<
  AutoDormCheckSettings,
  "schoolCheckInStartTime" | "schoolCheckInEndTime"
>;

const SCHOOL_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Shanghai",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function timeToMilliseconds(value: string): number | null {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = Number(match[3] || 0);
  if (hour > 23 || minute > 59 || second > 59) return null;
  return (hour * 3600 + minute * 60 + second) * 1000;
}

export function isWithinSchoolCheckInWindow(
  settings: SchoolCheckInWindow | null,
  now = new Date(),
): boolean {
  if (!settings || !Number.isFinite(now.getTime())) return false;
  const start = timeToMilliseconds(settings.schoolCheckInStartTime);
  const end = timeToMilliseconds(settings.schoolCheckInEndTime);
  if (start === null || end === null || start >= end) return false;
  const current = timeToMilliseconds(SCHOOL_TIME_FORMATTER.format(now));
  if (current === null) return false;
  const millisecond = current + now.getUTCMilliseconds();
  return millisecond >= start && millisecond <= end;
}

export function useSchoolCheckInWindow(settings: SchoolCheckInWindow | null) {
  const start = settings?.schoolCheckInStartTime;
  const end = settings?.schoolCheckInEndTime;
  const [open, setOpen] = useState(() => isWithinSchoolCheckInWindow(settings));

  useEffect(() => {
    const window =
      start && end
        ? { schoolCheckInStartTime: start, schoolCheckInEndTime: end }
        : null;
    const update = () => setOpen(isWithinSchoolCheckInWindow(window));
    update();
    const timer = globalThis.setInterval(update, 1000);
    document.addEventListener("visibilitychange", update);
    return () => {
      globalThis.clearInterval(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }, [start, end]);

  return open && isWithinSchoolCheckInWindow(settings);
}
